import { useEffect, useState } from "react";
import { api, apiError } from "@/lib/pos-api";
import { toast } from "sonner";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Plus, Pencil, Trash2, Tags } from "lucide-react";

const COLORS = ["#10B981", "#6366F1", "#3B82F6", "#F59E0B", "#EF4444", "#EC4899", "#8B5CF6", "#14B8A6"];

export default function Categories() {
  const [cats, setCats] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: "", color: COLORS[0], icon: "Package" });

  const load = async () => {
    try { setCats((await api.get("/categories")).data); }
    catch (e) { toast.error(apiError(e)); }
  };
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm({ name: "", color: COLORS[0], icon: "Package" }); setOpen(true); };
  const openEdit = (c) => { setEditing(c); setForm({ name: c.name, color: c.color, icon: c.icon }); setOpen(true); };

  const save = async () => {
    if (!form.name.trim()) return toast.error("Nama kategori wajib diisi");
    try {
      if (editing) await api.put(`/categories/${editing.id}`, form);
      else await api.post("/categories", form);
      toast.success("Kategori disimpan");
      setOpen(false); load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const remove = async (c) => {
    if (!window.confirm(`Hapus kategori "${c.name}"? Produk terkait akan tanpa kategori.`)) return;
    try { await api.delete(`/categories/${c.id}`); toast.success("Kategori dihapus"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  return (
    <>
      <PageHeader title="Kategori" subtitle={`${cats.length} kategori`}>
        <Button data-testid="add-category-btn" onClick={openNew} className="bg-emerald-500 hover:bg-emerald-600 text-slate-950 font-bold gap-2">
          <Plus className="h-4 w-4" /> Tambah Kategori
        </Button>
      </PageHeader>

      <div className="flex-1 overflow-y-auto p-5">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="category-management">
          {cats.map((c) => (
            <div key={c.id} data-testid={`category-card-${c.id}`} className="rounded-xl border border-slate-200 bg-slate-50 p-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="h-11 w-11 rounded-xl flex items-center justify-center" style={{ backgroundColor: c.color + "22", color: c.color }}>
                  <Tags className="h-5 w-5" />
                </div>
                <div>
                  <div className="font-semibold">{c.name}</div>
                  <div className="text-xs text-slate-500">{c.product_count} produk</div>
                </div>
              </div>
              <div className="flex gap-1">
                <button data-testid={`edit-category-${c.id}`} onClick={() => openEdit(c)} className="h-8 w-8 rounded-lg bg-[#F7F9FB] hover:bg-slate-200 flex items-center justify-center"><Pencil className="h-4 w-4 text-slate-600" /></button>
                <button data-testid={`delete-category-${c.id}`} onClick={() => remove(c)} className="h-8 w-8 rounded-lg bg-[#F7F9FB] hover:bg-red-500/20 flex items-center justify-center"><Trash2 className="h-4 w-4 text-red-600" /></button>
              </div>
            </div>
          ))}
          {cats.length === 0 && <p className="text-slate-500 col-span-full text-center py-10">Belum ada kategori</p>}
        </div>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="category-form-dialog" className="bg-[#F7F9FB] border-slate-300 text-slate-800 max-w-sm">
          <DialogHeader><DialogTitle className="font-heading">{editing ? "Edit Kategori" : "Tambah Kategori"}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label className="text-slate-600 text-xs">Nama</Label>
              <Input data-testid="category-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="bg-[#F7F9FB] border-slate-300" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-slate-600 text-xs">Warna</Label>
              <div className="flex flex-wrap gap-2">
                {COLORS.map((col) => (
                  <button key={col} onClick={() => setForm({ ...form, color: col })}
                    className={`h-9 w-9 rounded-lg border-2 ${form.color === col ? "border-white" : "border-transparent"}`}
                    style={{ backgroundColor: col }} />
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} className="border-slate-300 bg-transparent hover:bg-[#F7F9FB]">Batal</Button>
            <Button data-testid="save-category-btn" onClick={save} className="bg-emerald-500 hover:bg-emerald-600 text-slate-950 font-bold">Simpan</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
